import { ServerEnvironment, ServerSettingsEnvironment } from './server-settings'
import { Language, UserSettings } from './user-settings'

import type { AuthConfig } from '@dustin-web/auth-js'

const MOBILE_TYPES = ['mobile', 'tablet']

export type BaseLayoutInputPageConfig = {
  backgroundColor?: 'gray' | 'white'
}

export type BaseLayoutInputConfig = {
  showHeader?: boolean
  useDefaultBaseLayout?: boolean
  page?: BaseLayoutInputPageConfig
}

export type RequestServerContext = {
  site: Site
  isProxied: boolean
  isDevelopment: boolean
  correlationId: string
  clientIpAddress: string
  serverEnv: ServerEnvironment
  serverSettingsEnv: ServerSettingsEnvironment
  anonymousUserIdentifier: string
  esalesSessionKey: string
  host?: string
  siteUrl: string
  siteBaseUrl?: string
  deviceType?: string | null
  punchOutTicket?: string | null
  sessionUserIdentifier?: string | null
  percentageValueFromClientIp?: number
  userAgent?: string | null
}

export type RequestServerAppContext = RequestServerContext &
  ReturnType<typeof buildDerivedSiteContext>

export type CookieServerContext = {
  [key: string]: string
}

export type MicrositeSettings = {
  authConfig: AuthConfig & {
    siteType: string
    sourcingCompany: string
  }
  authServerConfig: AuthConfig
  graphqlConfig: {
    dustinHeaders: { [key: string]: string }
    graphqlServerUri: string
    graphqlUri: string
  }
  siteName: string
  isBusiness: boolean
}

export type ExpressLikeRequestContext = {
  header?: (name: string) => string
  headers: import('http').IncomingHttpHeaders
  hostname: string
  port: string
  query: import('querystring').ParsedUrlQuery
  // query: { [key: string]: string }
  auth: string | null
  hash: string | null
  host: string | null
  href: string
  path: string | null
  pathname: string | null
  protocol: string | null
  search: string | null
  slashes: boolean | null
}

export type MicrositePageContext = {
  cookieContext: CookieServerContext
  requestContext: RequestServerContext
  userSettings: UserSettings
  micrositeSettings: MicrositeSettings
  req: ExpressLikeRequestContext
  optimizelyDatafile?: string
}

export type MicrositeApiContext = {
  cookieContext: CookieServerContext
  requestContext: RequestServerContext
  userSettings: UserSettings
  req: ExpressLikeRequestContext
}

export type MicrositeAppContext = Omit<MicrositePageContext, 'requestContext'> & {
  requestContext: RequestServerAppContext
  query: { [key: string]: string }
}

export type Site =
  | 'DustinSweden'
  | 'DustinNorway'
  | 'DustinDenmark'
  | 'DustinFinland'
  | 'DustinNetherlands'
  | 'DustinBelgium'

export type SiteType = 'Business'

export type SourcingCompany = 'SE' | 'DK' | 'NO' | 'FI' | 'NL' | 'BE'

export enum PaymentMethod {
  KLARNA = 'KLARNA',
  SWISH = 'SWISH',
  MASTERCARD = 'MASTERCARD',
  VISA = 'VISA',
  VISADK = 'VISADK',
  TRUSTLY = 'TRUSTLY',
  IDEAL = 'IDEAL',
  PAYTRAIL = 'PAYTRAIL',
  MOBILEPLAY = 'MOBILEPLAY',
  INVOICE = 'INVOICE',
}

export const PaymentMethodsMetadata: Record<PaymentMethod, { type?: string; name: string }> = {
  [PaymentMethod.KLARNA]: {
    name: 'Klarna',
  },
  [PaymentMethod.SWISH]: {
    name: 'Swish',
  },
  [PaymentMethod.MASTERCARD]: {
    type: 'creditcard',
    name: 'MasterCard',
  },
  [PaymentMethod.VISA]: {
    type: 'creditcard',
    name: 'Visa',
  },
  [PaymentMethod.VISADK]: {
    type: 'bankdirect',
    name: 'Visa/Dankort',
  },
  [PaymentMethod.TRUSTLY]: {
    type: 'bankdirect',
    name: 'Trustly',
  },
  [PaymentMethod.IDEAL]: {
    type: 'bankdirect',
    name: 'iDeal',
  },
  [PaymentMethod.PAYTRAIL]: {
    type: 'bankdirect',
    name: 'Paytrail',
  },
  [PaymentMethod.MOBILEPLAY]: {
    name: 'Paytrail',
  },
  [PaymentMethod.INVOICE]: {
    type: 'invoice',
    name: 'Invoice',
  },
} as const

export type DeliveryMethod = {
  id: 'postnord' | 'bring' | 'posti' | 'postnl'
  displayName: string
}

export const getSourcingCompany = (site: Site): SourcingCompany => {
  switch (site) {
    case 'DustinNorway':
      return 'NO'
    case 'DustinDenmark':
      return 'DK'
    case 'DustinFinland':
      return 'FI'
    case 'DustinNetherlands':
      return 'NL'
    case 'DustinBelgium':
      return 'BE'
    case 'DustinSweden':
    default:
      return 'SE'
  }
}

export const getAvailableLanguages = (site: Site): Language[] => {
  switch (site) {
    case 'DustinNorway':
      return ['Norwegian', 'English']
    case 'DustinDenmark':
      return ['Danish', 'English']
    case 'DustinFinland':
      return ['Finnish', 'Swedish', 'English']
    case 'DustinNetherlands':
      return ['Dutch', 'English']
    case 'DustinBelgium':
      return ['Dutch', 'French', 'English']
    case 'DustinSweden':
    default:
      return ['Swedish', 'English']
  }
}

export const getSiteName = (site: Site): string => {
  switch (site) {
    case 'DustinSweden':
      return 'Dustin.se'
    case 'DustinNorway':
      return 'Dustin.no'
    case 'DustinDenmark':
      return 'Dustin.dk'
    case 'DustinFinland':
      return 'Dustin.fi'
    case 'DustinNetherlands':
      return 'Dustin.nl'
    case 'DustinBelgium':
      return 'Dustin.be'
    default:
      console.error(`Unknown site: ${site}`)
      return `${site}`
  }
}

export const getCountryCode = (sourcingCompany: SourcingCompany): string => {
  switch (sourcingCompany) {
    case 'SE':
      return 'SWE'
    case 'DK':
      return 'DNK'
    case 'FI':
      return 'FIN'
    case 'NL':
      return 'NLD'
    case 'NO':
      return 'NOR'
    case 'BE':
      return 'BEL'
    default:
      console.error(`Unknown sourcing company: ${sourcingCompany}`)
      return `${sourcingCompany}`
  }
}

export const THEME_COLOR = '#2e2e2e'

export const getSiteSelectorUrls = (site: Site, environment: string): string => {
  switch (environment.toLocaleLowerCase()) {
    case 'production':
      return getProdSiteSelectorUrls(site)
    case 'staging':
      return getStageSiteSelectorUrls(site)
    case 'test':
      return getTestSiteSelectorUrls(site)
    case 'local':
      return getLocalSiteSelectorUrls()
    default:
      return getLocalSiteSelectorUrls()
  }
}

const getProdSiteSelectorUrls = (site: Site) => {
  switch (site) {
    case 'DustinNorway':
      return 'https://www.dustin.no'
    case 'DustinDenmark':
      return 'https://www.dustin.dk'
    case 'DustinFinland':
      return 'https://www.dustin.fi'
    case 'DustinNetherlands':
      return 'https://www.dustin.nl'
    case 'DustinSweden':
      return 'https://www.dustin.se'
    case 'DustinBelgium':
      return 'https://www.dustin.be' // TODO: Site selector will soon be deprecated, not applicable for Belgium
    default:
      return 'https://www.dustin.se'
  }
}

const getStageSiteSelectorUrls = (site: Site) => {
  switch (site) {
    case 'DustinNorway':
      return 'https://stage.dustin.no.dustingroup.org'
    case 'DustinDenmark':
      return 'https://stage.dustin.dk.dustingroup.org'
    case 'DustinFinland':
      return 'https://stage.dustin.fi.dustingroup.org'
    case 'DustinNetherlands':
      return 'https://stage.dustin.nl.dustingroup.org/'
    case 'DustinSweden':
      return 'https://stage.dustin.se.dustingroup.org'
    case 'DustinBelgium': // TODO: Site selector will soon be deprecated, not applicable for Belgium
      return 'https://stage.dustin.be'
    default:
      return 'https://stage.dustin.se.dustingroup.org'
  }
}

const getTestSiteSelectorUrls = (site: Site) => {
  switch (site) {
    case 'DustinNorway':
      return 'https://test.dustin.no'
    case 'DustinDenmark':
      return 'https://test.dustin.dk'
    case 'DustinFinland':
      return 'https://test.dustin.fi'
    case 'DustinNetherlands':
      return 'https://test.dustin.nl'
    case 'DustinSweden':
      return 'https://test.dustin.se'
    case 'DustinBelgium': // TODO: Site selector will soon be deprecated, not applicable for Belgium
      return 'https://test.dustin.be'
    default:
      return 'https://test.dustin.se'
  }
}

const getLocalSiteSelectorUrls = () => {
  const localUrl = 'http://localhost:' + (process.env.PORT || '3000')
  return localUrl
}

export const getSiteFromHostname = (hostname: string) => {
  switch (hostname) {
    case 'localsebusiness':
    case 'local.dustin.se':
    case 'test.dustin.se':
    case 'stage.dustin.se.dustingroup.org':
    case 'dustin.se':
      return 'DustinSweden'

    case 'localfibusiness':
    case 'local.dustin.fi':
    case 'test.dustin.fi':
    case 'stage.dustin.fi.dustingroup.org':
    case 'dustin.fi':
      return 'DustinFinland'

    case 'localnobusiness':
    case 'local.dustin.no':
    case 'test.dustin.no':
    case 'stage.dustin.no.dustingroup.org':
    case 'dustin.no':
      return 'DustinNorway'

    case 'localdkbusiness':
    case 'local.dustin.dk':
    case 'test.dustin.dk':
    case 'stage.dustin.dk.dustingroup.org':
    case 'dustin.dk':
      return 'DustinDenmark'

    case 'localnlbusiness':
    case 'local.dustin.nl':
    case 'test.dustin.nl':
    case 'stage.dustin.com.dustingroup.org':
    case 'dustin.nl':
      return 'DustinNetherlands'

    case 'localbelgiumbusiness':
    case 'local.dustin.be':
    case 'dustin.be.local':
    case 'test.dustin.be':
    case 'dustin.be':
      return 'DustinBelgium'

    default:
      return 'DustinSweden'
  }
}

export const getCurrencyCode = (site: Site) => {
  switch (site) {
    case 'DustinNorway':
      return 'NOK'
    case 'DustinDenmark':
      return 'DKK'
    case 'DustinFinland':
    case 'DustinBelgium':
    case 'DustinNetherlands':
      return 'EUR'
    case 'DustinSweden':
      return 'SEK'
  }
}

export const getPaymentMethods = (site: Site): PaymentMethod[] => {
  switch (site) {
    case 'DustinNorway':
    case 'DustinSweden':
      return [
        PaymentMethod.MASTERCARD,
        PaymentMethod.VISA,
        PaymentMethod.TRUSTLY,
        PaymentMethod.INVOICE,
      ]
    case 'DustinDenmark':
      return [
        PaymentMethod.MASTERCARD,
        PaymentMethod.VISA,
        PaymentMethod.VISADK,
        PaymentMethod.TRUSTLY,
        PaymentMethod.INVOICE,
      ]
    case 'DustinFinland':
      return [
        PaymentMethod.MASTERCARD,
        PaymentMethod.VISA,
        PaymentMethod.PAYTRAIL,
        PaymentMethod.INVOICE,
      ]
    case 'DustinNetherlands':
      return [
        PaymentMethod.MASTERCARD,
        PaymentMethod.VISA,
        PaymentMethod.IDEAL,
        PaymentMethod.INVOICE,
      ]
    case 'DustinBelgium':
      return [PaymentMethod.MASTERCARD, PaymentMethod.VISA, PaymentMethod.IDEAL]
  }
}

export const getDeliveryMethods = (site: Site): DeliveryMethod[] => {
  switch (site) {
    case 'DustinNorway':
    case 'DustinDenmark':
      return [
        {
          id: 'bring',
          displayName: 'Bring',
        },
      ]
    case 'DustinFinland':
      return [
        {
          id: 'posti',
          displayName: 'Posti',
        },
      ]
    case 'DustinNetherlands':
    case 'DustinBelgium': // TODO: Implement delivery methods for Belgium
      return [
        {
          id: 'postnl',
          displayName: 'PostNL',
        },
      ]
    case 'DustinSweden':
      return [
        {
          id: 'postnord',
          displayName: 'Postnord',
        },
      ]
  }
}

export const getEnvironmentUrl = (baseUri: string, environment: string, isBrowserUri = false) => {
  if (isBrowserUri) {
    baseUri = baseUri.replace(/^http:\/\//i, '//')
  }

  switch (environment) {
    case 'local':
    case 'test':
      return `${baseUri}.test.cnt.dustintech.dev`
    case 'aws_local':
    case 'aws_test':
      return `${baseUri}.test.cnt.dustintech.dev`
    case 'staging':
    case 'aws_staging':
      return `${baseUri}.acc.cnt.dustintech.dev`
    case 'production':
    case 'aws_production':
      return `${baseUri}.cnt.dustintech.io`
    default:
      return `${baseUri}.test.cnt.dustintech.dev`
  }
}

export const getHeaderDomain = (path: string, sourcingCompany: SourcingCompany) => {
  const isNL = sourcingCompany === 'NL'
  const isBE = sourcingCompany === 'BE'

  const product = ['/group', '/product', '/accessories', '/compare', '/brands']
  const solutions = [getSolutionsUrl(sourcingCompany)]
  const knowledgeBank = [getKnowledgeBankUrl(sourcingCompany)]
  const customerService = [getServiceUrl(sourcingCompany)]

  if (product.some(item => path.startsWith(item))) {
    return 'Product'
  }

  if (knowledgeBank.some(item => path.startsWith(item))) {
    return 'Knowledge bank'
  }

  if (solutions.some(item => path.startsWith(item)) && !isNL && !isBE) {
    return 'Solutions'
  }

  if (customerService.some(item => path.startsWith(item))) {
    return 'Customer service'
  }

  return ''
}

export const getSolutionsUrl = (sourcingCompany: SourcingCompany) => {
  switch (sourcingCompany) {
    case 'NO':
      return '/tjenester'
    case 'NL':
      return `/oplossingen`
    default:
      return `/solutions`
  }
}

export const getGuidesUrl = (sourcingCompany: SourcingCompany) => {
  switch (sourcingCompany) {
    case 'SE':
      return '/editorial/guider'
    case 'NO':
      return '/editorial/guider'
    case 'DK':
      return '/editorial/guides'
    case 'FI':
      return '/editorial/oppaat'
    case 'NL':
      return '/artikelen'
    default:
      return '/editorial'
  }
}

export const getKnowledgeBankUrl = (sourcingCompany: SourcingCompany) => {
  const notInSolutions = sourcingCompany === 'NL'
  const solutionsUrl = notInSolutions ? '' : getSolutionsUrl(sourcingCompany)

  let url: string

  switch (sourcingCompany) {
    case 'DK':
      url = `${solutionsUrl}/vidensbanken`
      break
    case 'NO':
      url = `${solutionsUrl}/kunnskapsbanken`
      break
    case 'FI':
      url = `${solutionsUrl}/tietopankki`
      break
    case 'NL':
      url = `${solutionsUrl}/kennisbank`
      break
    default:
      url = `${solutionsUrl}/kunskapsbanken`
  }

  return url
}

export const getServiceUrl = (sourcingCompany: SourcingCompany) => {
  let url

  switch (sourcingCompany) {
    case 'NL':
      url = `/klantenservice`
      break
    default:
      url = `/service`
  }

  return url
}

export const buildDerivedSiteContext = (context: RequestServerContext) => {
  const sourcingCompany = getSourcingCompany(context.site)
  const siteType = 'Business' as SiteType
  const availableLanguages = getAvailableLanguages(context.site)

  const siteSelectorUrls = getSiteSelectorUrls(context.site, context.serverEnv)
  const businessSiteUrl = siteSelectorUrls
  const siteUrl = context.siteUrl
  const currencyCode = getCurrencyCode(context.site)
  const isMobile = !!context.deviceType && MOBILE_TYPES.includes(context.deviceType)

  return {
    ...context,
    sourcingCompany,
    siteType,
    availableLanguages,
    businessSiteUrl,
    siteUrl,
    currencyCode,
    isMobile,
  }
}
